import React, { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import { ToastMessage, ToastVariant } from 'components/ToastMessages/types';

interface ToastContextValue {
  toasts: ToastMessage[];
  showToast: (content: React.ReactElement | string, variant?: ToastVariant, timeout?: number) => void;
  removeToast: (id: number) => void;
  clearToasts: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

let toastIdCounter = 0;

const generateToastId = (): number => {
  toastIdCounter += 1;
  return toastIdCounter;
};

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const timersRef = useRef<Map<number, NodeJS.Timeout>>(new Map());

  const showToast = useCallback(
    (content: React.ReactElement | string, variant: ToastVariant = 'success', timeout = 5000) => {
      const id = generateToastId();
      const newToast: ToastMessage = {
        id,
        content,
        variant,
        timeout,
      };

      setToasts(prev => [...prev, newToast]);

      if (timeout) {
        const timerId = setTimeout(() => {
          setToasts(prev => prev.filter(toast => toast.id !== id));
          timersRef.current.delete(id);
        }, timeout);
        timersRef.current.set(id, timerId);
      }
    },
    []
  );

  const removeToast = useCallback((id: number) => {
    const timerId = timersRef.current.get(id);
    if (timerId) {
      clearTimeout(timerId);
      timersRef.current.delete(id);
    }
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearToasts = useCallback(() => {
    timersRef.current.forEach(timerId => clearTimeout(timerId));
    timersRef.current.clear();
    setToasts([]);
  }, []);

  useEffect(() => {
    return () => {
      timersRef.current.forEach(timerId => clearTimeout(timerId));
      timersRef.current.clear();
    };
  }, []);

  const value = {
    toasts,
    showToast,
    removeToast,
    clearToasts,
  };

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};
